/**
 * Barba.js SPA transition manager.
 *
 * Wires Barba's lifecycle hooks to the theme's page pipeline:
 *   leave        → dispatch event, fade/hide the outgoing container, reset ALM
 *   beforeEnter  → sync <body> classes, page styles/modals/scripts, admin bar,
 *                  Gravity Forms, and reset scroll to the top
 *   afterEnter   → restore the target hash, re-run page scripts, wait for media,
 *                  re-init ALM, resize Lenis, then scroll to the hash target
 *
 * Only `initializeBarbaTransitions` is public; the rest are internal helpers.
 */

import barba from '@barba/core';
import { trackPageView } from './google-analytics.js';
import { teardownAjaxLoadMore, reinitializeAjaxLoadMore, syncAlmConfigScripts } from './alm/ajax-load-more-manager.js';
import { refreshGravityForms } from './gravity-forms-manager.js';
import { monitorMediaLoading, resetPageLoaderProgress, updatePageLoaderProgress } from '../core/page-loader.js';
import { handleHashScroll } from '../core/scroll-manager.js';

// =============================================================================
// Debug logging
// =============================================================================

const NOOP_LOGGER = { log() {}, warn() {} };

/**
 * Create a namespaced logger that is silent unless debugging is enabled.
 * @param {boolean} debug - Whether to emit log output.
 * @return {{log: function, warn: function}} Logger with `log` and `warn`.
 */
function createLogger(debug) {
  if (!debug) return NOOP_LOGGER;
  return {
    log: (...args) => console.log('[Barba]', ...args),
    warn: (...args) => console.warn('[Barba]', ...args),
  };
}

// =============================================================================
// Next-page document parsing
// =============================================================================

/**
 * Parse the incoming page's HTML string into an inert Document, once.
 *
 * DOMParser documents do NOT load resources (no <img>/<iframe> prefetch) and do
 * NOT execute scripts, and they preserve correct <head>/<body> separation —
 * unlike assigning the string to a detached div's innerHTML, which parses in
 * "in-body" mode and speculatively fetches media.
 *
 * @param {string} nextHtml - The full HTML string of the next page.
 * @return {Document} The parsed, inert document.
 */
function parseNextDocument(nextHtml) {
  return new DOMParser().parseFromString(nextHtml, 'text/html');
}

/**
 * Return the parsed, inert next-page Document, parsing at most once per
 * transition. Barba passes the same `data` object through every hook, so the
 * result is memoized on it.
 * @param {object} data - Barba transition data object.
 * @return {Document|null} The parsed next document, or null if unavailable.
 */
function getNextDocument(data) {
  if (!data || !data.next || !data.next.html) return null;
  if (!data.__nextDoc) {
    data.__nextDoc = parseNextDocument(data.next.html);
  }
  return data.__nextDoc;
}

// =============================================================================
// Link prevention
// =============================================================================

/**
 * Determine whether Barba should ignore a link click (letting the browser or
 * another handler take it).
 * @param {object} event - Barba prevent event.
 * @param {HTMLAnchorElement} event.el - The clicked anchor element.
 * @return {boolean} True to prevent Barba from handling the click.
 */
function barbaPreventHandler({ el }) {
  // Normalize href/target to handle SVG links where these are SVGAnimatedString objects.
  const href = (el && typeof el.href === 'string') ? el.href : (el && el.getAttribute ? el.getAttribute('href') : '') || '';
  const target = (el && typeof el.target === 'string') ? el.target : (el && el.getAttribute ? el.getAttribute('target') : '') || '';

  // Missing element or empty href: not a Barba navigation.
  if (!el || !href) return true;

  // Search toggle, team modal triggers, and anchor-only links.
  if (el.closest && el.closest('.header__search-link')) return true;
  if (el.closest && el.closest('.team-listing, [data-team-modal-trigger]')) return true;
  if (href === '#' || href.endsWith('#')) return true;

  // Parent toggle links of dropdown menus: only prevent the direct toggle link,
  // so links inside the sub-menu are still handled by Barba.
  const menuItem = el.closest('li.menu-item-has-children');
  if (menuItem && el.parentElement === menuItem) return true;

  if (el.closest && el.closest('#wpadminbar')) return true; // WordPress Admin Bar
  if (target === '_blank') return true; // New-tab links
  if (/\.(pdf|zip|jpg|jpeg|png|gif|xml|svg|docx|xlsx|pptx|mp4|webm|mp3|ogg)$/i.test(href)) return true; // File downloads
  if (el.hasAttribute && el.hasAttribute('data-barba-prevent')) return true; // Explicitly prevented
  if (href.includes('wp-login.php') || href.includes('logout')) return true; // WP login/logout

  return false;
}

// =============================================================================
// DOM synchronisation helpers (beforeEnter / afterEnter)
// =============================================================================

/**
 * Copy the incoming page's <body> class onto the live document. Works around
 * Barba `sync: true` not reliably carrying body classes across a transition.
 * @param {Document} nextDoc - The parsed next-page document.
 * @param {object} logger - Namespaced logger.
 * @return {void}
 */
function syncManualBodyClasses(nextDoc, logger = NOOP_LOGGER) {
  if (!nextDoc || !nextDoc.body) {
    logger.warn('syncManualBodyClasses: no <body> in fetched document.');
    return;
  }
  document.body.className = nextDoc.body.className;
  logger.log('syncManualBodyClasses: body class synced ->', document.body.className);
}

/**
 * Remove tagged nodes from the live document and re-inject the incoming page's
 * equivalents into a destination element. Shared by page styles and modals.
 * @param {Document} nextDoc - The parsed next-page document.
 * @param {string} selector - Attribute selector identifying the tagged nodes.
 * @param {Element} destination - Where to append the incoming nodes.
 * @param {string} label - Human label for logging.
 * @param {object} logger - Namespaced logger.
 * @return {void}
 */
function swapTaggedNodes(nextDoc, selector, destination, label, logger = NOOP_LOGGER) {
  document.querySelectorAll(selector).forEach(node => {
    node.remove();
    logger.log(`Removed old ${label}${node.id ? ` #${node.id}` : ''}.`);
  });

  if (!nextDoc) return;
  const incoming = nextDoc.querySelectorAll(selector);
  incoming.forEach(node => destination.appendChild(node.cloneNode(true)));
  if (incoming.length) logger.log(`Injected ${incoming.length} ${label}(s).`);
}

/**
 * Swap page-specific inline <style data-barba-page-styles> tags into <head>.
 * @param {Document} nextDoc - The parsed next-page document.
 * @param {object} logger - Namespaced logger.
 * @return {void}
 */
function syncPageStyles(nextDoc, logger = NOOP_LOGGER) {
  swapTaggedNodes(nextDoc, 'style[data-barba-page-styles]', document.head, 'page style', logger);
}

/**
 * Swap page-specific modals ([data-barba-page-modal]) into <body>.
 * @param {Document} nextDoc - The parsed next-page document.
 * @param {object} logger - Namespaced logger.
 * @return {void}
 */
function syncPageModals(nextDoc, logger = NOOP_LOGGER) {
  swapTaggedNodes(nextDoc, '[data-barba-page-modal]', document.body, 'page modal', logger);
}

/**
 * Remove and re-inject page-specific scripts/tags for the given locations.
 * Scripts are re-created (not cloned) so they actually execute.
 * @param {Document} nextDoc - The parsed next-page document.
 * @param {string[]} locations - Locations to process: 'head', 'body-start', 'body-end'.
 * @param {object} logger - Namespaced logger.
 * @return {void}
 */
function syncPageScriptsByLocation(nextDoc, locations, logger = NOOP_LOGGER) {
  locations.forEach(location => {
    const selector = `[data-barba-page-script="${location}"]`;

    // Remove old elements from the live DOM.
    document.querySelectorAll(selector).forEach(el => {
      el.remove();
      logger.log(`syncScripts: removed old "${location}" element (${el.tagName}).`);
    });

    if (!nextDoc) return;
    const newElements = nextDoc.querySelectorAll(selector);
    if (!newElements.length) return;

    // Resolve the injection target for this location.
    const target = location === 'head' ? document.head : document.body;
    const insertBefore = location === 'body-start' ? document.body.firstChild : null;

    newElements.forEach(element => {
      let newEl;
      if (element.tagName === 'SCRIPT') {
        // A cloned <script> won't run; re-create it so the browser executes it.
        newEl = document.createElement('script');
        Array.from(element.attributes).forEach(attr => newEl.setAttribute(attr.name, attr.value));
        newEl.textContent = element.textContent;
        // Dynamically-created scripts default to async; force ordered execution for src scripts.
        if (newEl.src) newEl.async = false;
      } else {
        // <link>, <style>, <meta>, etc. clone fine.
        newEl = element.cloneNode(true);
      }

      if (insertBefore) {
        target.insertBefore(newEl, insertBefore);
      } else {
        target.appendChild(newEl);
      }
    });

    logger.log(`syncScripts: injected ${newElements.length} "${location}" element(s).`);
  });
}

/**
 * Replace the live WordPress Admin Bar with the incoming page's copy so edit
 * links and contextual shortcuts stay accurate after a transition.
 * @param {Document} nextDoc - The parsed next-page document.
 * @param {object} logger - Namespaced logger.
 * @return {void}
 */
function syncWpAdminBar(nextDoc, logger = NOOP_LOGGER) {
  if (!nextDoc) return;
  const currentAdminBar = document.getElementById('wpadminbar');
  const nextAdminBar = nextDoc.getElementById('wpadminbar');
  if (currentAdminBar && nextAdminBar) {
    currentAdminBar.replaceWith(nextAdminBar.cloneNode(true));
    logger.log('syncWpAdminBar: updated #wpadminbar.');
  }
}

// =============================================================================
// Hash handling
// =============================================================================

/**
 * Resolve the intended hash target for a transition, normalised to a single
 * leading '#'.
 *
 * Barba fetches the next page and, because the server response URL never carries
 * the client fragment, treats a hash-bearing URL as a redirect and replaceStates
 * to a hash-less URL. By afterEnter both `data.next.url.hash` and
 * `window.location.hash` are empty — but the original trigger (the clicked <a>)
 * still carries the intended fragment, so it is the primary source.
 *
 * @param {object} data - Barba transition data object.
 * @return {string} The normalised hash (e.g. "#purpose"), or "" if none.
 */
function resolveTransitionHash(data) {
  const trigger = data && data.trigger;
  const raw = (trigger && typeof trigger === 'object' && trigger.hash)
    ? trigger.hash
    : (data && data.next && data.next.url && data.next.url.hash ? `#${data.next.url.hash}` : '');
  return raw ? `#${raw.replace(/^#+/, '')}` : '';
}

/**
 * Restore a hash to the address bar without triggering navigation, so the URL
 * is shareable and downstream consumers (hash-scroll, modal hash handling) can
 * read `window.location.hash`.
 * @param {string} hash - The normalised hash to restore (e.g. "#purpose").
 * @return {void}
 */
function restoreHashToUrl(hash) {
  if (!hash || window.location.hash === hash) return;
  history.replaceState(history.state, '', window.location.pathname + window.location.search + hash);
}

// =============================================================================
// Initialisation
// =============================================================================

/**
 * Initialize Barba.js transitions with the theme's lifecycle hooks.
 * @param {object} options
 * @param {boolean} [options.debug] - Enable verbose transition logging.
 * @param {object} [options.timings] - Transition timing overrides.
 * @param {function} [options.onInitPageScripts] - Run per-page init (once + afterEnter).
 * @param {function} [options.onCleanupPageScripts] - Run per-page teardown (leave).
 * @param {function} [options.getLenis] - Accessor returning the active Lenis instance.
 * @return {void}
 */
export function initializeBarbaTransitions({ debug = false, timings, onInitPageScripts, onCleanupPageScripts, getLenis }) {
  const logger = createLogger(debug);
  const lenis = () => (getLenis ? getLenis() : null);

  barba.init({
    sync: true,
    debug,
    timeout: 7000,
    prevent: barbaPreventHandler,
    transitions: [{
      name: 'default',

      once(data) {
        logger.log('once.');
        if (onInitPageScripts) onInitPageScripts(data);
        trackPageView(false, debug);
      },

      async leave(data) {
        document.dispatchEvent(new CustomEvent('barba-leave-start', { detail: data }));

        // Tear down ALM up front so its stale scroll handler can't rewrite the URL
        // back to the archive mid-transition (see teardownAjaxLoadMore).
        teardownAjaxLoadMore(data.current && data.current.container, debug);

        document.documentElement.classList.remove('page-ready');
        document.body.classList.add('is-loading');
        resetPageLoaderProgress();
        setTimeout(() => updatePageLoaderProgress(20), timings?.leaveProgressNudge ?? 100);

        // Pin the outgoing container so it can fade out over the incoming one.
        const leavingContainer = data.current.container;
        if (leavingContainer) {
          Object.assign(leavingContainer.style, { position: 'fixed', top: '0', left: '0', width: '100%', opacity: '0' });
        }

        await new Promise(resolve => setTimeout(resolve, timings?.leaveMinDuration ?? 500));

        if (leavingContainer) leavingContainer.style.display = 'none';

        if (onCleanupPageScripts) onCleanupPageScripts(data);
      },

      beforeEnter(data) {
        logger.log('beforeEnter: body class at start ->', document.body.className);

        const nextDoc = getNextDocument(data);
        syncManualBodyClasses(nextDoc, logger);
        syncPageStyles(nextDoc, logger);
        syncPageModals(nextDoc, logger);
        syncWpAdminBar(nextDoc, logger);
        syncPageScriptsByLocation(nextDoc, ['head', 'body-start'], logger);
        refreshGravityForms(data.next.html, debug);

        // Reset scroll to the top before the new page reveals.
        const l = lenis();
        if (l) l.scrollTo(0, { immediate: true, force: true });
        else window.scrollTo(0, 0);
      },

      async afterEnter(data) {
        logger.log('afterEnter: body class at start ->', document.body.className);

        // Restore the intended hash before page scripts run (Barba strips it).
        const nextHash = resolveTransitionHash(data);
        restoreHashToUrl(nextHash);

        // Run body-end scripts, per-page init, and analytics — inside a View
        // Transition when available (and motion is not reduced).
        const applyEnterState = () => {
          syncPageScriptsByLocation(getNextDocument(data), ['body-end'], logger);
          if (onInitPageScripts) onInitPageScripts(data);
          trackPageView(data, debug);
        };

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (document.startViewTransition && !prefersReducedMotion) {
          await document.startViewTransition(applyEnterState).finished;
        } else {
          applyEnterState();
        }

        // Wait for media to load, then clear the loading state.
        try {
          await monitorMediaLoading(data.next.container, debug);
        } catch (err) {
          logger.warn('monitorMediaLoading failed; clearing loader anyway.', err);
        } finally {
          document.body.classList.remove('is-loading');
          document.documentElement.classList.add('page-ready');
        }

        // Re-execute the incoming page's ALM config (footer scripts, not swapped
        // by Barba) so per-instance globals are correct, then re-initialise ALM.
        syncAlmConfigScripts(getNextDocument(data), debug);
        reinitializeAjaxLoadMore(data.next.container, debug);

        const l = lenis();
        if (l) l.resize();

        // Scroll to the hash target now that media has loaded and Lenis has
        // re-measured — avoids landing short when late images reflow content.
        if (nextHash && l) handleHashScroll(l, nextHash, 0);

        await new Promise(resolve => setTimeout(resolve, timings?.loaderFadeOut ?? 500));
      },
    }],
  });
}
